import React, { useEffect, useMemo, useState } from 'react';

import { cssMap } from '@compiled/react';
import { useIntl } from 'react-intl';

// eslint-disable-next-line @atlaskit/editor/warn-no-restricted-imports
import { type EditorActions, WithEditorActions } from '@atlaskit/editor-core';
import { ComposableEditor } from '@atlaskit/editor-core/composable-editor';
import { EditorContext } from '@atlaskit/editor-core/editor-context';
import { usePreset } from '@atlaskit/editor-core/use-preset';
import CardClient from '@atlaskit/link-provider/client';
import { SmartCardProvider } from '@atlaskit/link-provider/smart-card-provider';
import { Box } from '@atlaskit/primitives/compiled';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import Spinner from '@atlaskit/spinner/spinner';
import {
	GenericErrorPage,
	HubErrorBoundary,
	useExtensionsActions,
	useExtensionsState,
	useHubContext,
} from '@atlassian/company-hub-common';
import {
	HubEditorConfigPanelEntryPoint,
	useHubEditorExtensionConfigPanel,
} from '@atlassian/hub-editor-config-panel';
import { type AkEditorLinkingProp, EditorLinkingFeatures } from '@atlassian/hub-editor-features';
import { getEmojiProvider, useHubExtensionProviders } from '@atlassian/hub-providers';

import { LINKING_FEATURES_CARD_OPTIONS } from '../../constants';
import { createHubEditorPreset } from '../../controllers/use-hub-editor-preset';
import { useMediaOptions } from '../../controllers/use-media-options';
import type { HubEditorAPI, HubEditorProps } from '../../types';
import messages from './messages';

interface EditorInternalProps extends HubEditorProps {
	editorActions: EditorActions;
}

const hubEditorStyles = cssMap({
	spinnerContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		height: '100vh',
	},
});

const EditorInternal = (props: EditorInternalProps) => {
	const intl = useIntl();
	const [editorApi, setEditorApi] = useState<HubEditorAPI>(undefined);
	const {
		editorActions,
		contentId,
		userId,
		cloudId,
		product,
		mediaProvider,
		setExternalEditorApi,
		additionalExtensionProviders = [],
		loadAdditionalExtensionProviders,
		onExtensionProvidersLoaded,
		PropsProvider,
		linkPickerOptions,
		isEditingLocked,
		isTemplateLocked,
	} = props;
	const smartCardClient = useMemo(() => new CardClient(), []);
	const { pageMediaConfig } = useHubContext();

	const { closeConfigPanel, openConfigPanel } = useHubEditorExtensionConfigPanel(editorActions);
	const [{ selectedExtensionLocalId }] = useExtensionsState();
	const { setIsInViewMode, setIsEditingLocked } = useExtensionsActions();
	const { featureFlags } = useHubContext();
	const hubExtensionProviders = useHubExtensionProviders({
		contentId,
		intl,
		PropsProvider,
		editorActions,
		openConfigPanel,
	});

	const { formatMessage } = useIntl();

	// Load additional extension providers when editor is ready; we need to inject
	// editor-specific props
	React.useEffect(() => {
		if (loadAdditionalExtensionProviders && editorActions) {
			loadAdditionalExtensionProviders({
				editorActions,
				openConfigPanel,
			});
		}
	}, [loadAdditionalExtensionProviders, editorActions, openConfigPanel]);

	// Combine hub extensions with additional extensions
	const allExtensionProviders = React.useMemo(() => {
		return [...hubExtensionProviders, ...additionalExtensionProviders];
	}, [hubExtensionProviders, additionalExtensionProviders]);

	// Notify parent when all extension providers (hub + additional) are available
	React.useEffect(() => {
		if (onExtensionProvidersLoaded && allExtensionProviders.length > 0) {
			onExtensionProvidersLoaded(allExtensionProviders);
		}
	}, [onExtensionProvidersLoaded, allExtensionProviders]);

	const mediaOptions = useMediaOptions({
		contentId,
		editorAPI: editorApi,
		customMediaProvider: mediaProvider,
		mediaConfig: pageMediaConfig,
	});

	const { preset, editorApi: presetEditorApi } = usePreset(() => {
		return createHubEditorPreset({
			...props,
			intl,
			mediaOptions,
			linkPickerOptions,
			featureFlags,
			isEditingLocked,
			isTemplateLocked,
		});
	}, [
		intl,
		props.hasEditPermission,
		isEditingLocked,
		isTemplateLocked,
		props.isLoomPluginEnabled,
		props.editorPluginAIDefinitionsEnabled,
		props.editorPluginHubTextColorEnabled,
		props.isDesktop,
		contentId,
		mediaOptions,
		linkPickerOptions,
	]);

	useEffect(() => {
		setIsInViewMode(false);
	}, [setIsInViewMode]);

	// Shared with extensions so their own edit affordances match the editor's lock.
	useEffect(() => {
		setIsEditingLocked(Boolean(isEditingLocked));
	}, [setIsEditingLocked, isEditingLocked]);

	useEffect(() => {
		setEditorApi(presetEditorApi);
		if (!setExternalEditorApi || !presetEditorApi) {
			return;
		}
		setExternalEditorApi(presetEditorApi);
	}, [presetEditorApi, setExternalEditorApi]);

	return (
		<SmartCardProvider product={product} client={smartCardClient}>
			<EditorLinkingFeatures
				product={product}
				cloudId={cloudId}
				options={LINKING_FEATURES_CARD_OPTIONS}
				linkPickerOptions={linkPickerOptions}
			>
				{(linkingFeatures: AkEditorLinkingProp) => {
					return hubExtensionProviders.length > 0 && additionalExtensionProviders.length > 0 ? (
						<ComposableEditor
							appearance="full-page"
							{...linkingFeatures}
							{...props}
							// The real read-only switch; `editorDisabled` only reports editability.
							disabled={isEditingLocked}
							preset={preset}
							{...(allExtensionProviders.length > 0
								? { extensionProviders: allExtensionProviders }
								: {})}
							editorActions={editorActions}
							contextPanel={
								<HubEditorConfigPanelEntryPoint
									localId={selectedExtensionLocalId || ''}
									closeConfigPanel={closeConfigPanel}
									editorActions={editorActions}
									contentId={contentId}
									editorApi={editorApi}
									propsProvider={PropsProvider}
								/>
							}
							emojiProvider={getEmojiProvider({
								cloudId,
								userId,
							})}
						/>
					) : (
						<UFOLoadHold name="hub-extensions-loader">
							<Box xcss={hubEditorStyles.spinnerContainer}>
								<Spinner
									size={'large'}
									label={formatMessage(messages.hubStudioLoadingSpinnerLabel)}
								/>
							</Box>
						</UFOLoadHold>
					);
				}}
			</EditorLinkingFeatures>
		</SmartCardProvider>
	);
};

const HubEditor = (props: HubEditorProps): React.JSX.Element => {
	return (
		<HubErrorBoundary
			packageName="@atlassian/hub-editor"
			id="hub-editor"
			fallback={<GenericErrorPage />}
		>
			<EditorContext>
				<WithEditorActions
					render={(editorActions: EditorActions) => (
						<EditorInternal editorActions={editorActions} {...props} />
					)}
				/>
			</EditorContext>
		</HubErrorBoundary>
	);
};

export default HubEditor;
